import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Klasowy Error Boundary — łapie błędy renderowania w poddrzewie
 * i pokazuje fallback po polsku zamiast białego ekranu.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
            <h1 className="text-xl font-semibold text-slate-800 mb-2">
              Coś poszło nie tak
            </h1>
            <p className="text-sm text-slate-600 mb-4">
              Dane wprowadzone w tej sesji są zachowane.
            </p>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-800 text-sm"
            >
              Odśwież stronę
            </button>
            {this.state.error && (
              <details className="mt-4 text-left text-xs text-slate-500">
                <summary className="cursor-pointer">Szczegóły błędu</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
